/**
 * The types the chat's board client tools and the Boards feature share.
 *
 * The client tools live in the shared layer (`shared/domain/agentThread/clientTools/`) and may not import feature
 * code. The parser, the query executors, the DuckDB engine, and the Vega chart code all live in `src/Boards/`.
 * This module holds the interface the tools call and a loader the app layer registers through
 * `registerBoardAuthoringService`, so the Boards chunk (and with it DuckDB and Vega) loads only when a board tool or
 * its confirmation card first needs it.
 *
 * `shared/services/ViewingContextService/liveVisualizerState.ts` is a shared registry of the same kind, which a feature
 * fills in.
 *
 * Tool results use JSON-compatible values rather than Arrow tables. Dry-run reports limit samples and columns; a
 * `read` may return a whole panel or definition and up to `READ_ROWS_CAP` rows of a stage as the page fetched them.
 */

import type { QueryClient } from "@tanstack/react-query";

import type { BoardService } from "@/shared/domain/boards";
import type { DeclaredBoardCalendar } from "@/shared/domain/boards/boardCalendar";
import type { BoardTimeRangeMode } from "@/shared/domain/boards/boardTimeRangeMode";
import type { ViewerControlChoices } from "@/shared/domain/boards/viewerControlChoices";
import type { CustomFieldService } from "@/shared/domain/customFields/CustomFieldService";
import type { MetricsService } from "@/shared/domain/metrics/MetricsService";
import type { QueryService } from "@/shared/domain/query/QueryService";
import type { SessionService } from "@/shared/domain/sessions/SessionService";
import type { TopicService } from "@/shared/domain/topics/TopicService";

import type {
  InteractionDiagnostic,
  InteractionOutcome,
  InteractionPreview,
  InteractionRequest,
  InteractionValue,
  PanelInteraction,
  ResultContext,
  ResultRevision,
  SelectionDeclaration,
  SerializedInteractionValue,
  SerializedSelectionClause,
} from "./interactionTypes";

/**
 * Wall-clock budget for one dry run, timed by the authoring service's `dryRun`. A stage still running when the budget
 * runs out is reported as `dry-run-timed-out`, and the run still returns a report.
 */
export const BOARD_DRY_RUN_TIMEOUT_MS = 60_000;

/**
 * Services the dry run needs. The board tools take them from `ClientToolDeps`.
 */
export interface DryRunServices {
  customFieldService: CustomFieldService;
  metricsService: MetricsService;
  queryService: QueryService;
  sessionService: SessionService;
  topicService: TopicService;
}

/**
 * What reaching a board takes: the resource client and the org that holds the
 * board.
 *
 * Kept apart from the dry run's context because the two needs do not overlap. A
 * read of a board runs no queries and needs no cache, and a dry run reaches no
 * board: it runs a definition that has been handed to it.
 */
export interface BoardServiceForOrg {
  boardService: BoardService;
  /**
   * The org whose boards are read and written. The same org the dry run runs its
   * queries in, since both come from the person's current organization.
   */
  orgId: string;
}

/** Everything a dry run needs beyond the definition itself. */
export interface AuthoringServiceContext {
  orgId: string;
  services: DryRunServices;
  /** Queries run through this client under the executors' own keys, so the page's first render reuses them. */
  queryClient: QueryClient;
  /** Aborting it rejects the dry run with the signal's reason, and no report is produced. */
  signal: AbortSignal;
}

/** How a stage or panel came out of a dry run. `waiting` is a stage that did not run because a control it reads has
 *  no value, neither one the viewer chose nor a default, or a panel that shows one or whose chart spec reads such a
 *  control; nothing about it was checked, and it does not block. */
export type DryRunSeverity = "ok" | "warnings" | "waiting" | "failed";

/** What a stage or panel the dry run did not check waits for. */
export interface DryRunWait {
  /** The captions of the controls with no value, as the control bar shows them. */
  controls: string[];
  /** Why nothing was checked, and what would let the dry run check it, for the model. */
  message: string;
}

/** A failure with its `kind`, named the way the board page names it so the model and the user read the same words. */
export interface DryRunFailure {
  kind: string;
  message: string;
  /** Declaration validation details, when the failure starts before execution. */
  issues?: string[];
  /**
   * Where in the definition the failure is, as dot-separated member names and list positions from the definition's
   * top (`panels.2.interactions.0.target.keys.1`). Present on a failure of a panel's interactions, selection
   * bindings, mark lineages or detail target.
   */
  at?: string;
  /**
   * Indices in the definition's `stages` list of the stages whose own failures start this one: the stage itself, or
   * for a blocked stage, the failed stages it reads.
   */
  rootIndices?: number[];
  /** Indices in `stages` from this stage to one of those stages: its own index, then a root's index if it is blocked. */
  path?: number[];
  /**
   * Present when running the same definition again can pass, because nothing in the definition is known to have
   * caused the failure (a request that did not complete, a read that was stopped, or a SQL engine that did not load,
   * restarted after an internal error, or stopped on an error that names nothing in the definition), or because the
   * dry run ran out of time (`dry-run-timed-out`).
   */
  rerun_can_pass?: true;
  /**
   * A stage's failure as the board words it for a person: a declaration's failure as the stage's card words it, or a
   * control value that cannot be used as a panel showing the stage words it. The confirmation cards show it under the
   * stage's line; `message` and `issues` are the forms written for the chat.
   */
  besideStage?: { message: string; issues: string[] };
  /**
   * A panel's settings issues, or why a control value its chart's spec reads cannot be used, as the board words them
   * for a person, shown under the panel's line on the cards.
   */
  besidePanel?: { issues: string[] };
}

export interface DryRunColumn {
  name: string;
  type: string;
}

/** One stage's outcome in the dry run; a stage runs once however many panels bind it. */
export interface DryRunStageReport {
  /**
   * The declared id, or null when the declaration carries none. A stage with no id cannot be named by a stage
   * operation or a binding; `address` says where it is in the definition's `stages` list.
   */
  stage: string | null;
  /** The declaration's position in `stages`, and that position written as `stages[index]`. */
  index?: number;
  address?: string;
  /** The stage's name as a person reads it, or its position written as `stages[index]` when it has none. */
  stageName: string;
  kind: string;
  status: DryRunSeverity;
  /** The stage's name and one sentence saying what its rows are, when the stage produced a result, as `captionOf` in `Boards/chatAuthoring/dryRunDefinition.ts` writes it. */
  caption?: string;
  rows?: number;
  truncated?: boolean;
  columns?: DryRunColumn[];
  /** How many of the stage's columns `columns` leaves out, when the report's size limit cut the list. */
  columns_omitted?: number;
  /** At most two rows; string values cut at 60 characters. Dropped first when the report exceeds its size limit. */
  sample?: Record<string, unknown>[];
  failure?: DryRunFailure;
  /** Present when the stage's status is `waiting`. */
  waiting_for?: DryRunWait;
  warnings?: string[];
}

/** Whether a chart's tooltip or legend selection is asked for by its spec ("authored") or supplied by the board
 *  ("default"). */
export type ChartBehaviorOrigin = "authored" | "default";

export interface DryRunChartReport {
  compiled: boolean;
  /** Mark items counted in the headless scenegraph after the bound results were injected. */
  marks?: number;
  errors: string[];
  warnings: string[];
  /**
   * The name and message of an internal error (such as a `TypeError`) that stopped the spec from being built into a
   * view. They name no fault in the spec, so they are not among `errors`. Present only when `compiled` is false.
   */
  internal_error?: string;
  /**
   * The unit views whose marks the page cannot read, which show no tooltip, each with the reason. A view inside
   * `repeat` is always listed, and so is a text view whose spec says nothing of a tooltip and that no stored mark
   * lineage names; a view whose spec turns its tooltip off never is. Absent when the chart did not compile.
   */
  tooltips?: {
    unavailable: {
      /** The view's address in the definition, such as `panels.0.spec.layer.1`. */
      view: string;
      /** "authored" when the view's spec asks for a tooltip, "default" when it says nothing of one. */
      origin: ChartBehaviorOrigin;
      reason: string;
    }[];
  };
  /**
   * The chart's discrete legends whose entries select series when clicked: "default" for one the board makes
   * selectable, which dims the series left out and changes nothing outside the chart; "authored" for one a Vega-Lite
   * param binds (`bind: "legend"`). Each names the scales it shows, which Vega-Lite names after the encoding channel,
   * such as `color` or `concat_1_color`, and the column whose values it lists when it lists one column's. Absent when
   * the chart did not compile.
   */
  selectable_legends?: {
    title?: string;
    scales: string[];
    field?: string;
    origin: ChartBehaviorOrigin;
  }[];
}

export interface DryRunPanelReport {
  /** The declared id, or null when the panel carries none, which no panel operation can then name. */
  id: string | null;
  /** The panel's position in `panels`, present only when it has no id. */
  index?: number;
  kind: string;
  status: DryRunSeverity;
  /** Present when the panel failed to parse; nothing was checked for it. */
  parse?: DryRunFailure;
  /** The ids of the stages the panel binds, whose outcomes are in the report's `stages`. */
  stages: string[];
  chart?: DryRunChartReport;
  /** Blocking failures beyond the stages' own (chart field missing, chart run error, pivot failed). */
  failures: DryRunFailure[];
  /**
   * Faults in how the panel draws (its chart, pivot, map bindings or title) that the saved board already has: the
   * update leaves the panel, the stages and controls it depends on and the time range as saved. They do not block
   * the update, and the panel's status is `warnings`. Present only when an update's dry run found one.
   */
  saved_board_faults?: DryRunFailure[];
  /** Present when the panel's chart spec reads a control with no value; the panel's status is then `waiting`. A
   *  panel that waits only because a stage it shows waits has the status without this member. */
  waiting_for?: DryRunWait;
  warnings: string[];
}

/** How the dry run used the values a viewer chose in the controls of the board on screen (`DryRunOptions`). */
export interface DryRunViewerChoices {
  /** The captions of the controls that held the viewer's value in the run. */
  used: string[];
  /** The controls whose value the viewer chose no longer fits the control in this definition (it does not read as
   *  the control's type, or is not one of a field control's `options`), so the dry run took them as having no
   *  value: each one's caption, and whether its default was used instead. */
  unfit: { control: string; default_used: boolean }[];
}

export interface DryRunReport {
  status: "ok" | "blocked";
  /**
   * Present only when the definition's top-level members could not be read; nothing ran, and `stages` and `panels`
   * are empty.
   */
  definition_problems?: string[];
  /** The top-level members `definition_problems` are in, such as `time_range` or `stages`, each once; present with
   *  `definition_problems`, and empty when every problem is with the definition as a whole. */
  definition_problem_members?: string[];
  /** `definition_problems` as the board words them for a person, each once; the confirmation cards show them. */
  definition_problem_reasons?: string[];
  /** The board's time range the stages ran over, resolved to absolute instants, as ISO 8601
   *  text in the board's time zone with its offset. */
  resolved_time_range?: { start: string; end: string };
  /** Every declared stage's outcome, once, in declaration order; a stage no panel binds still runs. */
  stages: DryRunStageReport[];
  panels: DryRunPanelReport[];
  /** Present when the dry run was given the viewer's choices. */
  viewer_choices?: DryRunViewerChoices;
  /**
   * What the run noted about the board as a whole rather than about one
   * panel or one stage, each a kind-prefixed sentence, such as a section that
   * declares a selection of its own that nothing inside it publishes. Never blocking:
   * the board renders, and the note says what it will not do.
   */
  warnings: string[];
  /** Set when the report was shortened to fit its size limit; `note` then says what was left out and how to get it. */
  truncated?: true;
  note?: string;
}

export type PanelUpdateOperation =
  | { op: "replace"; panel_id: string; panel: unknown }
  | { op: "add"; panel_id: string; panel: unknown }
  | { op: "remove"; panel_id: string }
  /**
   * Changes how the panel takes part in the board's selections (`participation`)
   * and where its own gesture goes (`publish`) without resending the panel. A
   * member sent replaces the stored one whole, a member omitted is kept, and
   * `null` removes it. A panel without `participation` takes `filter` for
   * every selection; without `publish`, its gesture goes to its section's
   * selection of that name when the section declares one, else to the
   * board's. The dry run validates both members when it parses the patched
   * definition.
   */
  | {
      op: "set_participation_and_publish";
      panel_id: string;
      participation?: Record<string, unknown> | null;
      publish?: Record<string, unknown> | null;
    };

export type StageUpdateOperation =
  | {
      op: "add_stage";
      stage_id: string;
      stage: unknown;
      after_stage_id?: string;
    }
  | { op: "replace_stage"; stage_id: string; stage: unknown }
  | { op: "rename_stage"; stage_id: string; name: string }
  | { op: "remove_stage"; stage_id: string };

/**
 * One stage a panel binds, for `set_panels_filtered_by_control`: the column
 * the control-filter matches and what it holds, or `leave_unfiltered` to
 * keep that stage unfiltered while the panel's other stages are filtered.
 * The tool's input form of `BindingColumnChoice` in the Boards feature.
 */
export type BindingColumnChoiceInput = {
  panel_id: string;
  stage_id: string;
} & (
  | { column: string; column_holds: "value" | "entity-id" }
  | { leave_unfiltered: true }
);

/**
 * Sets the panels whose rows the value control `control_id` filters to
 * exactly `panel_ids`, the full set: control-filter stages are added,
 * reused, copied or removed (`setPanelsFilteredByControl` in the Boards
 * feature). `columns` gives, for a stage a listed panel binds, the column
 * to match or that the stage stays unfiltered (`BindingColumnChoiceInput`).
 */
export interface SetPanelsFilteredByControlOperation {
  op: "set_panels_filtered_by_control";
  control_id: string;
  panel_ids: string[];
  columns?: BindingColumnChoiceInput[];
}

export type UpdateOperation =
  | PanelUpdateOperation
  | StageUpdateOperation
  | SetPanelsFilteredByControlOperation;

export function isStageUpdateOperation(
  operation: UpdateOperation,
): operation is StageUpdateOperation {
  return "stage_id" in operation;
}

export function isPanelUpdateOperation(
  operation: UpdateOperation,
): operation is PanelUpdateOperation {
  return "panel_id" in operation;
}

export const MIXED_STAGE_EDITS_REFUSAL =
  'This update carries both the whole "stages" list and stage operations (add_stage, replace_stage, rename_stage, ' +
  'remove_stage), and one update takes only one of the two. Send the stage operations without "stages", or send ' +
  '"stages" with no stage operation.';

/**
 * The two panel members a `set_participation_and_publish` writes, named once so the
 * tool's input reading and `applyUpdateOperations` in the Boards feature walk
 * the same pair.
 */
export const PARTICIPATION_AND_PUBLISH_MEMBERS = [
  "participation",
  "publish",
] as const;

export interface UpdateRequest {
  boardId: string;
  title?: string;
  timeRange?: unknown;
  /** The board's calendar member, replacing the stored one; the feature validates it in the dry run. */
  calendar?: unknown;
  /** The board's whole sections array as it should be after the update; the feature validates it in the dry run. */
  sections?: unknown[];
  operations: UpdateOperation[];
  /**
   * The board's whole selections list, replacing the stored one; an empty list removes every declared selection.
   * Validated together with the patched definition.
   */
  selections?: SelectionDeclaration[];
  /** The board's whole stages list, replacing the stored one; an empty list removes every stage. */
  stages?: unknown[];
}

export type UpdateOutcome =
  /** The stored definition or operations could not produce a patched definition without a refusal; nothing was written. */
  | { status: "refused"; reasons: string[] }
  /**
   * The patched definition passed its dry run and the platform then refused to store it, with a reason the
   * platform wrote for the caller. Nothing was written, and sending the same update again is refused again.
   */
  | { status: "save-refused"; reason: string }
  /** The patched definition failed its dry run; nothing was written. */
  | { status: "blocked"; report: DryRunReport }
  /**
   * Someone else replaced the board's definition after this update read it, so the patched definition does not
   * derive from what is stored. The platform refused the write under its own lock and nothing was stored. The
   * message names the revision the update read, and the stored one when the refusal carries the stored record; the
   * caller chooses what to do next, and this outcome chooses nothing.
   */
  | { status: "board-changed"; message: string }
  /** The board was deleted after this update read it, so there is no board to save to and nothing was stored. */
  | { status: "board-deleted"; boardId: string }
  | {
      status: "saved";
      boardId: string;
      title?: string;
      report: DryRunReport;
    }
  /** The save never came back with an answer, so whether it was stored is unknown. */
  | { status: "write-failed"; message: string };

export interface BoardListing {
  id: string;
  /** The board's name, which the board list and the board page show. */
  name: string;
  /** The definition's own title, when it carries one; equal to `name` whenever it is there. */
  title?: string;
  /** True for a board draft, which the person has not saved yet. */
  draft: boolean;
  /** ISO 8601 instant the board was last changed. */
  changedAt: string;
  panelCount: number;
}

/**
 * One stage a stored board declares, read from its `stages` array as stored, so a stage that fails to parse is still
 * listed.
 */
export interface StoredStage {
  /**
   * The declared id, or null when the declaration carries none. A stage with no id cannot be named by a stage
   * operation or a binding; `address` says where it is in the definition's `stages` list.
   */
  id: string | null;
  name: string;
  kind: string;
  /** The declaration's position in `stages`, and that position written as `stages[index]`. */
  index?: number;
  address?: string;
  /**
   * The stages this one reads: each id written in its `inputs`, with `index` only when the id names exactly one stage,
   * or, when it declares no `inputs`, the stage before it (`defaulted: true`).
   */
  inputs?: { id?: string; index?: number; defaulted: boolean }[];
  status?: "valid" | "invalid" | "blocked";
  failure?: DryRunFailure;
}

/**
 * One stage of a rendered panel that the panel shows a card for in place of rows, in the card's words: the stage
 * failed, waits for a choice in a control, or was stopped before it had rows.
 */
export interface StoredPanelStageCard {
  /** The stage's id, which the binding names. */
  stage: string;
  kind: string;
  message: string;
}

export interface StoredPanelStatus {
  interactions?: PanelInteraction[];
  interactionDiagnostics?: InteractionDiagnostic[];
  id?: string;
  index: number;
  kind?: string;
  title?: string;
  /**
   * The panel's stored `placement` when it is an object: `x`, `y`, `width`, and `height` in canvas units, and the
   * `section` it sits in when it has one. Read as stored, so a panel that fails to parse still reports where it is.
   */
  placement?: Record<string, unknown>;
  /**
   * How the panel takes part in each selection, keyed by selection name
   * (`filter`, `highlight`, or `ignore`), as stored; absent when the panel
   * takes `filter` for every selection.
   */
  participation?: Record<string, unknown>;
  /**
   * Where the panel's own gesture for a selection goes, keyed by selection
   * name (`outer` sends it to the board's selection from inside a section
   * that declares a selection of the same name), as stored; absent when each
   * gesture goes to its section's selection of that name when the section
   * declares one, else to the board's.
   */
  publish?: Record<string, unknown>;
  /** The ids of the stages the panel binds, read as stored off its `bindings`. */
  stages: string[];
  parse: "valid" | DryRunFailure;
  /**
   * The stage cards of the panel as its board page reports them, present only while that board is on screen in
   * this tab (the page registers them; only the page runs the stages). Absent means unknown, not none.
   */
  stageCards?: StoredPanelStageCard[];
}

/** One value as the chat reads it: a value a control holds, or one field of a point clause's row. */
export type ScalarValue = string | number | boolean;

/**
 * What a control holds when the chat reads it. `has-value` carries at least one value; `no-value` has none. A
 * control-filter of an optional control passes every row while the control has no value, but SQL and spec references
 * and field controls still need a value. `pending` is a control on a custom field whose list of the organization's
 * custom fields has not arrived, or a field control whose list of fields has not; `unusable` is a custom field the
 * organization does not have or has with another type, address-bar text that does not read as the declared value
 * type, or a field control whose chosen field is not in its list.
 */
export interface OnScreenControlValue {
  status: "has-value" | "no-value" | "pending" | "unusable";
  values: ScalarValue[];
  taggedValues?: SerializedInteractionValue[];
  /** One label per value: the static option's label, else the value's own text. */
  labels: string[];
}

/**
 * One control of a stored board: a `value-control` or `field-control` stage, with the value it holds while the
 * board is on screen. `read` carries this shape.
 */
export interface StoredControlStatus {
  /** The control stage's id, which `inputs`, `replace_stage` and `remove_stage` name it by. */
  id: string;
  /** The control stage's name, which text writes as `$name`. */
  name: string;
  /**
   * The stage's body as parsed, so the chat can rewrite the control from what it reads. A field control's `options`
   * are left out and a value control's static `options` are replaced by their count; a `detail: "full"` read returns
   * them in the definition.
   */
  body: Record<string, unknown>;
  /** The caption the control bar shows for the control. */
  caption: string;
  /**
   * Each panel that reads the control, and how: `rows` says whether every stage the panel binds (`all`), some
   * (`some`) or none (`none`) passes through a control-filter of it, `byName` whether the panel's text or a stage
   * it reads names it as `$name`, and `places` where: the panel's own `title` or chart `spec`, then the `sql`,
   * `expressions`, `group-by` key or topic `field-path`s of each stage its rows come from, by `stageId`. A panel
   * that reads it in neither way is left out.
   */
  uses: {
    panelId: string;
    rows: "all" | "some" | "none";
    byName: boolean;
    places: readonly (
      | { kind: "title" | "spec" }
      | {
          kind: "sql" | "expressions" | "group-by" | "field-path";
          stageId: string;
        }
    )[];
  }[];
  /**
   * The control's value, present only while the board is on screen in this tab (the page registers it; a
   * viewer's choice lives in the page address and never in the definition). Absent means unknown, not none.
   */
  onScreen?: OnScreenControlValue;
}

/**
 * The type tag beside each point value of a clause, by field position; a `bigint` value travels as its decimal
 * string.
 */
export type ClauseValueType = "string" | "number" | "boolean" | "bigint";

/** Which selection a published clause is in and who published it. */
interface PublishedClauseBase {
  /** The selection the clause is published in, such as `focus` or `brush`. */
  selection: string;
  /** `board` for one of the board's own selections, or the id of the section that declares the selection. */
  scope: string;
  /** `<origin>/<detail>`: the publishing panel's id, or one of `address-bar`, `chat`, `defaults`, `control`. */
  source: string;
  /** Panel ids the clause does not narrow (the publisher itself); empty for a clause the chat published. */
  panels_not_narrowed: string[];
  /** Display text the publisher attached, such as an entity's name beside its id. */
  label?: string;
  /**
   * The name the control bar shows for each of the clause's keys, such as the title of the chart axis a brush covers;
   * a key with none is shown as written.
   */
  key_titles?: Record<string, string>;
}

/**
 * One clause published on the board on screen, in the selection store's clause shape (`ClauseValue`) with the time
 * range bounds named `start_epoch_ms` and `end_epoch_ms`. Live viewing state: it is never part of a board definition
 * and is never written into one.
 */
export type PublishedClause =
  | (PublishedClauseBase & { type: "keyed"; value: SerializedSelectionClause })
  | (PublishedClauseBase & {
      type: "point";
      fields: string[];
      /** One entry per selected row, each with a value per field. */
      values: ScalarValue[][];
      valueTypes: ClauseValueType[];
    })
  | (PublishedClauseBase & {
      type: "interval";
      fields: string[];
      /** One `[low, high]` extent of numbers per field, by position; on a time column, epoch milliseconds. */
      values: [number, number][];
    })
  | (PublishedClauseBase & {
      type: "time_range";
      mode: BoardTimeRangeMode;
      /** The time range's bounds as epoch milliseconds. */
      start_epoch_ms: number;
      end_epoch_ms: number;
    });

/** How much of a stored board `read` returns: the per-panel summary, or the whole definition with it. */
export type ReadDetail = "summary" | "full";

/** Most rows a read of one panel returns. */
export const READ_ROWS_CAP = 50;

export interface ReadRequest {
  boardId?: string;
  panelId?: string;
  /** Defaults to `summary`; ignored with `panelId`, which always returns the whole panel. */
  detail?: ReadDetail;
  /**
   * With `panelId`: sample at most `READ_ROWS_CAP` rows of a bound stage, in stage-result order.
   * Selections applied during data fetching may already affect the result. The rows are the stage's
   * result, before any transform in a chart's spec or a table's pivot, and are available only while
   * the board is on screen in this tab.
   */
  rows?: number;
  /** With `rows`: which of the stages the panel binds, by id; defaults to the first stage the panel binds. */
  stage?: string;
}

export type ReadResult =
  | { status: "list"; boards: BoardListing[] }
  /**
   * There is no board at this id that this person can open. Absence and a teammate's personal board are one answer on
   * purpose: the platform answers both the same way, so that a read cannot tell someone that a board they may not
   * open exists.
   */
  | { status: "not-found"; boardId: string }
  | {
      status: "panel-not-found";
      boardId: string;
      panelId: string;
      panelIds: string[];
    }
  | {
      status: "board";
      boardId: string;
      /** The mounted instance's id and definition digest, present only while the board is on screen in this tab. */
      onScreenBoard?: { instanceId: string; definitionDigest: string };
      /** The board's name, which the board list and the board page show. */
      name: string;
      title?: string;
      /** True for a board draft, which the person has not saved yet. */
      draft: boolean;
      /** ISO 8601 instant the board was last changed. */
      changedAt: string;
      /** The board's time range, or null when the definition's top level fails to parse. */
      timeRange: BoardTimeRangeMode | null;
      /** The board's calendar, its time zone and week start, or null when the definition's top level fails to parse. */
      calendar: Required<DeclaredBoardCalendar> | null;
      /**
       * The canvas's sections as stored (`id`, `title`, `x`, `y`, `width`, `height`, and the `selections` a section
       * declares for itself), when the definition carries any.
       */
      sections?: unknown[];
      /** The board's declared stages by id, name and body kind, read as stored, when the definition carries any. */
      stages?: StoredStage[];
      /**
       * Present when the definition's top level fails to parse; a `detail: "full"` read still returns the stored
       * definition so the chat can fix it.
       */
      invalidTopLevel?: { issues: string[] };
      /**
       * Only with `detail: "full"`: the stored definition, or with each panel's `spec` replaced by a note when the
       * serialized size exceeds `READ_SPEC_ELISION_BYTES` (`Boards/chatAuthoring/authoringService.ts`).
       */
      definition?: unknown;
      panels: StoredPanelStatus[];
      /**
       * Panel ids whose `spec` was elided from `definition`; a read with `panel_id` returns that panel's whole entry,
       * `spec` included.
       */
      specsElided?: string[];
      /**
       * The board's controls, each with its value while the board is on screen in this tab. Absent when the
       * definition's top level fails to parse.
       */
      controls?: StoredControlStatus[];
      /**
       * The clauses published on the board, present only while it is on screen in this tab.
       * Absent means unknown, not none.
       */
      clauses?: PublishedClause[];
    }
  | {
      status: "panel";
      /**
       * Each mark lineage output of the panel with the precondition an interaction request on it must carry. Reading
       * them runs no interaction.
       */
      markLineageOutputs?: {
        markLineageId: string;
        precondition: import("./markLineageTypes").MarkLineageInteractionPrecondition;
        rows: {
          /** The key the renderer looks this drawn row up by; `outputRowKey` is the row's key in the prepared output. */
          rowKey: string;
          outputRowKey: string;
          rowOrigin?: "observed" | "imputed";
          renderedGroup?: import("./markLineageTypes").RenderedGroup;
          values: Record<
            string,
            import("./markLineageTypes").SerializedExactValue
          >;
        }[];
        complete: boolean;
        diagnostics: InteractionDiagnostic[];
      }[];
      /**
       * Each binding the panel displays on the mounted board, with its rows as tagged values, its result revision
       * and its result context; `rows` below is a sample of one bound stage's fetched rows instead.
       */
      interactionResults?: {
        instanceId: string;
        definitionDigest: string;
        bindingName: string;
        revision: ResultRevision;
        resultContext: ResultContext;
        rows: {
          rowKey: string;
          values: Record<string, SerializedInteractionValue>;
        }[];
      }[];
      boardId: string;
      panel: unknown;
      parse: StoredPanelStatus;
      /** The first rows of one of the stages the panel binds as the page fetched them, when `rows` was asked and available. */
      rows?: Record<string, unknown>[];
      /** How many rows that stage holds in all. */
      rowsTotal?: number;
      /** Which stage the rows come from and how they were read, or why none could be returned. */
      rowsNote?: string;
    };

export type PatchOutcome =
  | {
      status: "refused";
      /** Why, for the chat, which receives them when the person approves. */
      reasons: string[];
      /** Why, in words for a person, when the Boards feature has them; the confirmation card shows them. */
      cardReasons?: string[];
    }
  | {
      status: "patched";
      definition: unknown;
      /** The stored definition the patch was applied to. */
      storedDefinition: unknown;
      /**
       * One entry per operation of the request, in order: the sentence the confirmation card shows for it, worded by
       * the Boards feature from the definitions before and after; undefined where the card words the operation from the
       * request.
       */
      operationSentences: readonly (string | undefined)[];
    };

/** Options of a dry run. */
export interface DryRunOptions {
  /**
   * The stored definition an update was applied to. A panel it holds with the same members is dry-run as stored: its
   * chart template is not expanded again, so the update does not rewrite a panel it did not touch. Every stage runs
   * and every panel is checked whatever this holds, a stage no panel binds included, since a saved declaration does
   * not prove that it succeeds on the data this dry run reads; the query cache may answer a request already made. A fault
   * in how a panel draws that the saved board already has is reported as `saved_board_faults` and does not block.
   */
  storedDefinition?: unknown;
  /**
   * The values the viewer chose in the controls of the board on screen (`ViewerControlChoices`). Each stage runs with
   * the control values the page would show once the definition is saved: the viewer's value for a control, when it
   * still fits that control (`DryRunViewerChoices.unfit`), then the control's default. A stage with neither is not
   * run and is reported as waiting for that control.
   */
  viewerChoices?: ViewerControlChoices;
}

/** The dry run as `update` runs it; `dryRun` by default, or a caller's function over the same arguments. */
export type DryRunFunction = (
  definition: unknown,
  ctx: AuthoringServiceContext,
  options?: DryRunOptions,
) => Promise<DryRunReport>;

export interface UpdateOptions {
  /**
   * Produces the dry-run report of the patched definition. The update tool passes a function that reads the
   * confirmation card's cached report, so an approval reuses the run the card already made instead of running every
   * stage and chart again; the authoring service's own `dryRun` runs when nothing is passed.
   */
  dryRun?: DryRunFunction;
}

/** A chart panel whose template could not be turned into a spec, by its position in `panels`. */
export interface TemplateExpansionFailure {
  index: number;
  /** The panel's `id` when it carries a string one. */
  id?: string;
  /** The template's own words: an unknown name, or the param it refused. */
  message: string;
}

/** A definition with every template-authored chart panel carrying its emitted spec, and the panels that could not. */
export interface TemplateExpansion {
  definition: unknown;
  failures: TemplateExpansionFailure[];
}

/**
 * What the chat asks to publish: a point clause on ids, an interval between two numbers or two instants on one column,
 * both ends included, or the removal of its own clause.
 */
export type ClauseRequest =
  | { type: "point"; field: string; values: string[] }
  | { type: "number-interval"; field: string; bounds: [number, number] }
  | {
      type: "instant-interval";
      field: string;
      bounds: [
        Extract<InteractionValue, { type: "instant" }>,
        Extract<InteractionValue, { type: "instant" }>,
      ];
    }
  | { type: "clear" };

export interface PublishRequest {
  boardId: string;
  selection: string;
  clause: ClauseRequest;
  /** Display text for the control bar beside the ids, such as the entity's name. */
  label?: string;
}

export type PublishOutcome =
  /** The board is not on screen in this tab, so there is no mounted instance to publish into. */
  | { status: "not-on-screen"; boardId: string }
  /** The named selection is not one the chat may publish into; `selections` lists the ones it may. */
  | {
      status: "selection-not-accepted";
      selection: string;
      selections: string[];
    }
  | {
      status: "published";
      /** The selection the request named. */
      selection: string;
      /** The source recorded with the clause, which the control bar labels as the chat's. */
      source: string;
      /** Every clause published on the board after the write, across every selection. */
      clauses: PublishedClause[];
    }
  | {
      status: "cleared";
      /** The selection the request named. */
      selection: string;
      /** Whether the clear removed a clause the chat had published; false when a later gesture already replaced it. */
      removed: boolean;
      clauses: PublishedClause[];
    };

/**
 * Whether the chat may change a stage of the panel editor's draft:
 * `changeable` when the draft lists it and nothing outside the draft reads
 * it, `read-outside-draft` when the draft lists it and something outside the
 * draft reads it too, `read-by-draft` when the draft does not list it and a
 * draft stage reads it.
 */
export type PanelDraftStageChangeability =
  "changeable" | "read-outside-draft" | "read-by-draft";

/** One stage of the panel editor's draft, or a board stage the draft reads. */
export interface PanelDraftStageView {
  id: string;
  name: string;
  kind: string;
  changeability: PanelDraftStageChangeability;
  /** What outside the draft reads the stage, for `read-outside-draft`. */
  readBy?: string[];
  /** The stage's declaration as Save would write it. */
  declaration: unknown;
  /** How far the editor has run the stage; absent for a board stage. */
  runStatus?: string;
  columns?: string[];
  columnsOmitted?: number;
  /** The problems the editor shows for the stage, when it shows any. */
  issues?: string[];
}

/** The panel editor's unsaved draft, as the chat reads it. */
export interface PanelDraftView {
  boardId: string;
  editorOpeningId: string;
  /** The id the draft's panel is saved under, also while it has no type. */
  draftId: string;
  hasPanelKind: boolean;
  /** True when the editor was opened on a saved panel. */
  openedOnSavedPanel: boolean;
  /** Names this state of the draft; an update passes it back. */
  version: string;
  /** The draft's panel, or null while it has no panel kind. */
  panel: unknown;
  /** The problems the editor shows that belong to no one stage. */
  issues: string[];
  stages: PanelDraftStageView[];
}

export type PanelDraftReadResult =
  /** No panel editor is open in this tab. */
  | { status: "no-editor" }
  /** The open editor is not the one `editorOpeningId` names. */
  | { status: "other-editor"; boardId: string }
  | { status: "draft"; draft: PanelDraftView };

export interface PanelDraftUpdateRequest {
  editorOpeningId: string;
  /** The version of the read or update the operations were written from. */
  version: string;
  operations: UpdateOperation[];
}

export type PanelDraftUpdateOutcome =
  | { status: "no-editor" }
  | { status: "other-editor"; boardId: string }
  /** The version is not one of the latest this editor gave out. */
  | { status: "unknown-version"; version: string }
  /** What the operations name changed in the editor since that version. */
  | { status: "conflict"; changed: string[] }
  /** The editor was still applying an earlier change, so this one was not
   *  applied. */
  | { status: "busy" }
  /** The operations reach outside the draft, or cannot be applied. */
  | { status: "refused"; reasons: string[] }
  | {
      status: "applied";
      /** What changed, in the words the editor's notice shows. */
      summary: string;
      changedStageIds: string[];
      /** The draft as the editor holds it after the change. */
      draft: PanelDraftView;
    };

export interface BoardAuthoringService {
  /**
   * Turns every chart panel carrying `template` and no `spec` into the same panel with the emitted `spec` beside the
   * template record and emitted bindings, as the panel editor stores them. Synchronous, and leaves `definition` as it
   * is. A panel whose template cannot be expanded is returned as sent and named in `failures`; every other panel is
   * the same object. `dryRun` and `update` apply this themselves; the create tool calls it after a dry run that is
   * not blocked, then `completeBuilderValueTypes`, and saves the result as the new board, so the board stores the
   * specs the dry run checked.
   */
  expand(definition: unknown): TemplateExpansion;
  /**
   * `definition` with the value type the panel editor would give each column a builder chart draws where the
   * chart's params name none, read from the stage results of a dry run of `definition`, which runs only when some
   * builder chart lacks one. `dryRun` and `update` apply this themselves; the create tool applies it to what `expand`
   * returns.
   */
  completeBuilderValueTypes(
    definition: unknown,
    ctx: AuthoringServiceContext,
  ): Promise<unknown>;
  /** The dry run. Runs every stage and every chart headlessly; writes nothing. */
  dryRun: DryRunFunction;
  /**
   * Reads the board and prepares the patched definition. When the update renames a stage, it first dry-runs the
   * stored definition to read the combine inputs' columns the rename needs. Writes nothing.
   */
  patch(
    request: UpdateRequest,
    ctx: AuthoringServiceContext,
    boards: BoardServiceForOrg,
  ): Promise<PatchOutcome>;
  /**
   * Lists the boards the person can open, or returns one board's per-panel summary (its whole definition with
   * `detail: "full"`), or one of its panels whole with that panel's status.
   */
  read(request: ReadRequest, boards: BoardServiceForOrg): Promise<ReadResult>;
  /**
   * Applies panel and stage operations to a board, dry-runs the outcome with the board's stored definition as
   * `storedDefinition`, and saves it unless the dry run's status is `blocked`.
   *
   * One read of the board drives everything after it: the dry run takes the definition that read returned as its
   * `storedDefinition`, and the save carries the revision it returned, so the write is refused if anyone replaced the
   * definition in the meantime. Nothing is written in that case, and nothing is chosen on the person's behalf.
   */
  update(
    request: UpdateRequest,
    ctx: AuthoringServiceContext,
    boards: BoardServiceForOrg,
    options?: UpdateOptions,
  ): Promise<UpdateOutcome>;
  /**
   * Publishes one clause into a selection of the board on screen under the chat's source, or clears the chat's clause
   * from that selection. Synchronous, and writes nothing durable: the clause is state of the board's interaction
   * runtime, which the control bar shows and the page forgets when it leaves the board.
   */
  publish(request: PublishRequest): PublishOutcome;
  /** Checks an unsaved definition's authored interaction without fetching rows or changing what the page shows. */
  previewInteraction(request: {
    definition: unknown;
    panelId: string;
    interactionId: string;
  }): InteractionPreview | undefined;
  /** Runs an interaction request on the mounted board its instance id names; refused when no board on screen has it. */
  dispatchInteraction(request: InteractionRequest): InteractionOutcome;
  /** Reads the unsaved draft of the panel editor open in this tab. Writes nothing. */
  readPanelDraft(request: { editorOpeningId: string }): PanelDraftReadResult;
  /**
   * Applies panel and stage operations to the panel editor's unsaved draft. The editor shows the change as an edit the
   * person can undo, and nothing is saved.
   */
  updatePanelDraft(
    request: PanelDraftUpdateRequest,
  ): Promise<PanelDraftUpdateOutcome>;
}

type ServiceLoader = () => Promise<BoardAuthoringService>;

let registeredLoader: ServiceLoader | undefined;
let loaded: Promise<BoardAuthoringService> | undefined;

/**
 * Sets how the authoring service is loaded and forgets any service already loaded. `main.tsx` passes a dynamic import
 * of `Boards/chatAuthoring/authoringService`; tests pass a loader of a service built for them, through
 * `registerNodeAuthoringService` in `test/_support/nodeAuthoringService.ts`, `registerFakeService` in
 * `test/_support/boardToolFakes.ts`, or directly.
 */
export function registerBoardAuthoringService(loader: ServiceLoader): void {
  registeredLoader = loader;
  loaded = undefined;
}

/** Loads the authoring service on first use and memoizes it; a failed load is not memoized so a retry can succeed. */
export function loadBoardAuthoringService(): Promise<BoardAuthoringService> {
  if (registeredLoader === undefined) {
    return Promise.reject(
      new Error("Board authoring is not available in this build."),
    );
  }
  if (loaded === undefined) {
    const loader = registeredLoader;
    loaded = loader().catch((error: unknown) => {
      loaded = undefined;
      throw error;
    });
  }
  return loaded;
}
